import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Spinner } from "@/components/ui";
import { ProtectedRoute } from "./ProtectedRoute";
import { CapabilityRoute } from "./CapabilityRoute";
import { PlatformProtectedRoute } from "@/platform/PlatformProtectedRoute";
import { PlatformCapabilityRoute } from "@/platform/PlatformCapabilityRoute";

// Every route below is its own chunk (React.lazy + dynamic import): an
// unauthenticated visitor on /login never downloads billing/procurement/
// payroll/etc, and an authenticated user only pays for the bounded contexts
// they actually open. Only the route guards above stay eager - they're tiny
// and run on every navigation, so splitting them buys nothing but an extra
// round trip. See vite.config.ts for the vendor chunk that sits alongside
// these (react/router/query/i18n, cached independently of app code).
const AppLayout = lazy(() =>
  import("@/components/layout/AppLayout").then((m) => ({ default: m.AppLayout })),
);
const LoginPage = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const AdminLoginPage = lazy(() =>
  import("@/pages/AdminLoginPage").then((m) => ({ default: m.AdminLoginPage })),
);
const DemoStartPage = lazy(() =>
  import("@/pages/DemoStartPage").then((m) => ({ default: m.DemoStartPage })),
);
const DemoLoginPage = lazy(() =>
  import("@/pages/DemoLoginPage").then((m) => ({ default: m.DemoLoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import("@/pages/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })),
);
const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const CustomersPage = lazy(() =>
  import("@/features/customers/CustomersPage").then((m) => ({ default: m.CustomersPage })),
);
const CustomerDetailPage = lazy(() =>
  import("@/features/customers/CustomerDetailPage").then((m) => ({
    default: m.CustomerDetailPage,
  })),
);
const VehiclesPage = lazy(() =>
  import("@/features/customers/VehiclesPage").then((m) => ({ default: m.VehiclesPage })),
);
const VehicleDetailPage = lazy(() =>
  import("@/features/customers/VehicleDetailPage").then((m) => ({ default: m.VehicleDetailPage })),
);
const SalePage = lazy(() =>
  import("@/features/sales/SalePage").then((m) => ({ default: m.SalePage })),
);
const JobCardsPage = lazy(() =>
  import("@/features/jobcards/JobCardsPage").then((m) => ({ default: m.JobCardsPage })),
);
const JobCardDetailPage = lazy(() =>
  import("@/features/jobcards/JobCardDetailPage").then((m) => ({
    default: m.JobCardDetailPage,
  })),
);
const ApprovalPage = lazy(() =>
  import("@/features/jobcards/ApprovalPage").then((m) => ({ default: m.ApprovalPage })),
);
const PartsPage = lazy(() =>
  import("@/features/inventory/PartsPage").then((m) => ({ default: m.PartsPage })),
);
const CatalogPage = lazy(() =>
  import("@/features/inventory/CatalogPage").then((m) => ({ default: m.CatalogPage })),
);
const QrLabelsPage = lazy(() =>
  import("@/features/inventory/QrLabelsPage").then((m) => ({ default: m.QrLabelsPage })),
);
const InvoicesPage = lazy(() =>
  import("@/features/billing/InvoicesPage").then((m) => ({ default: m.InvoicesPage })),
);
const InvoiceDetailPage = lazy(() =>
  import("@/features/billing/InvoiceDetailPage").then((m) => ({ default: m.InvoiceDetailPage })),
);
const QuotesPage = lazy(() =>
  import("@/features/billing/QuotesPage").then((m) => ({ default: m.QuotesPage })),
);
const QuoteDetailPage = lazy(() =>
  import("@/features/billing/QuoteDetailPage").then((m) => ({ default: m.QuoteDetailPage })),
);
const ProcurementPage = lazy(() =>
  import("@/features/procurement/ProcurementPage").then((m) => ({ default: m.ProcurementPage })),
);
const PurchaseOrderDetailPage = lazy(() =>
  import("@/features/procurement/PurchaseOrderDetailPage").then((m) => ({
    default: m.PurchaseOrderDetailPage,
  })),
);
const SupplierDetailPage = lazy(() =>
  import("@/features/procurement/SupplierDetailPage").then((m) => ({
    default: m.SupplierDetailPage,
  })),
);
const ImportPage = lazy(() =>
  import("@/features/import/ImportPage").then((m) => ({ default: m.ImportPage })),
);
const ReportsPage = lazy(() =>
  import("@/features/reporting/ReportsPage").then((m) => ({ default: m.ReportsPage })),
);
const DaybookPage = lazy(() =>
  import("@/features/accounting/DaybookPage").then((m) => ({ default: m.DaybookPage })),
);
const InvoiceMovementsPage = lazy(() =>
  import("@/features/accounting/InvoiceMovementsPage").then((m) => ({ default: m.InvoiceMovementsPage })),
);
const AccountingPage = lazy(() =>
  import("@/features/accounting/AccountingPage").then((m) => ({ default: m.AccountingPage })),
);
const ExpensePage = lazy(() =>
  import("@/features/expense/ExpensePage").then((m) => ({ default: m.ExpensePage })),
);
const ExpenseCategoriesPage = lazy(() =>
  import("@/features/expense/ExpenseCategoriesPage").then((m) => ({ default: m.ExpenseCategoriesPage })),
);
const PayrollPage = lazy(() =>
  import("@/features/payroll/PayrollPage").then((m) => ({ default: m.PayrollPage })),
);
const PayRunsPage = lazy(() =>
  import("@/features/payroll/PayRunsPage").then((m) => ({ default: m.PayRunsPage })),
);
const SettingsPage = lazy(() =>
  import("@/features/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const AccountPage = lazy(() =>
  import("@/features/account/AccountPage").then((m) => ({ default: m.AccountPage })),
);
const PrintersPage = lazy(() =>
  import("@/features/printing/PrintersPage").then((m) => ({ default: m.PrintersPage })),
);
const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);
const PlatformLayout = lazy(() =>
  import("@/platform/PlatformLayout").then((m) => ({ default: m.PlatformLayout })),
);
const PlatformDashboard = lazy(() =>
  import("@/platform/PlatformDashboard").then((m) => ({ default: m.PlatformDashboard })),
);
const TenantsPage = lazy(() =>
  import("@/platform/TenantsPage").then((m) => ({ default: m.TenantsPage })),
);
const LeadsPage = lazy(() =>
  import("@/platform/LeadsPage").then((m) => ({ default: m.LeadsPage })),
);
const DemoSessionsPage = lazy(() =>
  import("@/platform/DemoSessionsPage").then((m) => ({ default: m.DemoSessionsPage })),
);
const OnboardTenantPage = lazy(() =>
  import("@/platform/OnboardTenantPage").then((m) => ({ default: m.OnboardTenantPage })),
);
const TenantDetailPage = lazy(() =>
  import("@/platform/TenantDetailPage").then((m) => ({ default: m.TenantDetailPage })),
);
const AdminsPage = lazy(() =>
  import("@/platform/AdminsPage").then((m) => ({ default: m.AdminsPage })),
);
const PlatformSettingsPage = lazy(() =>
  import("@/platform/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const SmsDeliveryPage = lazy(() =>
  import("@/platform/SmsDeliveryPage").then((m) => ({ default: m.SmsDeliveryPage })),
);
const StuckEventsPage = lazy(() =>
  import("@/platform/StuckEventsPage").then((m) => ({ default: m.StuckEventsPage })),
);
const PlatformCatalogPage = lazy(() =>
  import("@/platform/CatalogPage").then((m) => ({ default: m.CatalogPage })),
);
const PackagingPage = lazy(() =>
  import("@/platform/PackagingPage").then((m) => ({ default: m.PackagingPage })),
);

// Full-screen fallback for the outer boundary (first paint of an unauthenticated
// page, or the very first entry into /app or /platform before their layout
// chunk arrives). AppLayout/PlatformLayout have their own nested Suspense
// around just the <Outlet/>, so switching between already-loaded pages only
// reloads the content pane, not the whole chrome.
function RouteFallback() {
  return (
    <div className="grid h-dvh place-items-center bg-bg text-muted">
      <Spinner size={28} />
    </div>
  );
}

// Route map. This is the APP build served on the app subdomain (app.klutch.lk):
// its root goes straight to "/login" (an authenticated visitor is forwarded on to
// "/app" by the login page). The marketing landing lives in a separate repo
// (klutch.lk-landing, served on klutch.lk), so no landing page lives here. The
// authenticated app lives under "/app" (dashboard + per-context pages).
export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Platform / super-admin sign-in (6.6): a separate route from the tenant
            /login, matching the separate session axis (authkit_padmin cookie). */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Public "try it live" entry point: the marketing site (separate repo,
            klutch.lk) links its CTA here. Provisions an ephemeral, isolated demo
            tenant and signs the visitor straight into /app. */}
        <Route path="/demo" element={<DemoStartPage />} />
        <Route path="/demo/login" element={<DemoLoginPage />} />

        {/* Self-service password reset, both axes (6). Public + unauthenticated. */}
        {/* Public, code-authenticated customer estimate approval (no session). The
            short "/a/:code" path keeps the SMS/WhatsApp link tidy. */}
        <Route path="/a/:code" element={<ApprovalPage />} />

        <Route path="/password/forgot" element={<ForgotPasswordPage />} />
        <Route path="/password/reset" element={<ResetPasswordPage />} />
        <Route
          path="/platform/password/forgot"
          element={<ForgotPasswordPage platform />}
        />
        <Route
          path="/platform/password/reset"
          element={<ResetPasswordPage platform />}
        />

        {/* Session gate, then the RBAC gate (§6.3): CapabilityRoute redirects a
            user who lands on a screen their role cannot use to the dashboard. It
            reads the one route→capability table (auth/routeAccess), which the
            sidebar reads too, so menu visibility and URL access never disagree. */}
        <Route path="/app" element={<ProtectedRoute />}>
          <Route element={<CapabilityRoute />}>
            <Route element={<AppLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="sales" element={<SalePage />} />
              <Route path="jobs" element={<JobCardsPage />} />
              {/* New Job is now a modal on the list; keep the old intake URL working. */}
              <Route path="jobs/intake" element={<Navigate to="/app/jobs" replace />} />
              <Route path="jobs/:id" element={<JobCardDetailPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:id" element={<CustomerDetailPage />} />
              <Route path="vehicles" element={<VehiclesPage />} />
              <Route path="vehicles/:id" element={<VehicleDetailPage />} />
              <Route path="parts" element={<PartsPage />} />
              <Route path="catalog" element={<CatalogPage />} />
              <Route path="labels" element={<QrLabelsPage />} />
              <Route path="quotes" element={<QuotesPage />} />
              <Route path="quotes/:id" element={<QuoteDetailPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="invoices/:id" element={<InvoiceDetailPage />} />
              <Route path="procurement" element={<ProcurementPage />} />
              <Route
                path="procurement/orders/:id"
                element={<PurchaseOrderDetailPage />}
              />
              <Route
                path="procurement/suppliers/:id"
                element={<SupplierDetailPage />}
              />
              <Route path="import" element={<ImportPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="ledger" element={<DaybookPage />} />
              <Route path="ledger/movements" element={<InvoiceMovementsPage />} />
              <Route path="accounting" element={<AccountingPage />} />
              <Route path="expenses" element={<ExpensePage />} />
              <Route path="expenses/categories" element={<ExpenseCategoriesPage />} />
              <Route path="payroll" element={<PayrollPage />} />
              <Route path="payroll/runs" element={<PayRunsPage />} />
              <Route path="printers" element={<PrintersPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="account" element={<AccountPage />} />
            </Route>
          </Route>
        </Route>

        {/* Platform / super-admin: separate session axis AND a separate capability
            axis (6.6). Same two-gate shape as /app - session, then capabilities -
            but nothing is shared between the axes except the wildcard rule. */}
        <Route path="/platform" element={<PlatformProtectedRoute />}>
          <Route element={<PlatformCapabilityRoute />}>
            <Route element={<PlatformLayout />}>
              <Route index element={<PlatformDashboard />} />
              <Route path="tenants" element={<TenantsPage />} />
              <Route path="tenants/onboard" element={<OnboardTenantPage />} />
              <Route path="tenants/:id" element={<TenantDetailPage />} />
              <Route path="demo" element={<DemoSessionsPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="packaging" element={<PackagingPage />} />
              <Route path="catalog" element={<PlatformCatalogPage />} />
              <Route path="sms" element={<SmsDeliveryPage />} />
              <Route path="ops/stuck-events" element={<StuckEventsPage />} />
              <Route path="admins" element={<AdminsPage />} />
              <Route path="settings" element={<PlatformSettingsPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
